let metaConfig = { enabled: false, pixelId: "" };
let pixelPromise;
const META_CLICK_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

export function createMetaFbc(fbclid, createdAt = Date.now()) {
  const clickId = String(fbclid || "").trim();
  const timestamp = Number(createdAt);
  if (!/^[a-zA-Z0-9._:-]{8,500}$/.test(clickId) || !Number.isFinite(timestamp) || timestamp <= 0) {
    return "";
  }

  return `fb.1.${Math.floor(timestamp)}.${clickId}`;
}

function rememberMetaAdClick() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const fbclid = new URLSearchParams(window.location.search).get("fbclid") || "";
  const fbc = createMetaFbc(fbclid);
  if (!fbc) {
    return;
  }

  const hasFbc = document.cookie
    .split(";")
    .map((item) => item.trim())
    .some((item) => item.startsWith("_fbc="));
  if (hasFbc) {
    return;
  }

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `_fbc=${fbc}; Path=/; Max-Age=${META_CLICK_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

export function createMetaEventId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `gelato-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function loadPixel(pixelId) {
  if (!pixelId || typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (!pixelPromise) {
    pixelPromise = new Promise((resolve) => {
      const fbq =
        window.fbq ||
        ((...args) => {
          if (fbq.callMethod) {
            fbq.callMethod(...args);
          } else {
            fbq.queue.push(args);
          }
        });

      window.fbq = fbq;
      window._fbq = window._fbq || window.fbq;
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = "2.0";
      window.fbq.queue = window.fbq.queue || [];

      window.fbq("init", pixelId);

      const existingScript = document.querySelector('script[src*="connect.facebook.net"][src*="fbevents.js"]');
      if (existingScript) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      script.dataset.metaPixel = pixelId;
      script.addEventListener("load", () => resolve(true), { once: true });
      script.addEventListener("error", () => resolve(false), { once: true });
      document.head.appendChild(script);
    });
  }

  return pixelPromise;
}

export async function configureMetaTracking(config = {}) {
  metaConfig = {
    enabled: Boolean(config.enabled && config.pixelId),
    pixelId: String(config.pixelId || "")
  };

  if (!metaConfig.enabled) {
    return false;
  }

  rememberMetaAdClick();
  loadPixel(metaConfig.pixelId).catch(() => false);
  return true;
}

export async function trackMetaEvent(eventName, customData = {}, options = {}) {
  if (!metaConfig.enabled || typeof window === "undefined") {
    return "";
  }

  const eventId = options.eventId || createMetaEventId();
  const eventSourceUrl = window.location.href;

  try {
    loadPixel(metaConfig.pixelId).catch(() => false);
    window.fbq?.("track", eventName, customData, { eventID: eventId });
  } catch (_error) {
    // The server event can still be delivered when the browser pixel is blocked.
  }

  if (options.server !== false) {
    fetch("/api/meta/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName, eventId, eventSourceUrl, customData }),
      keepalive: true
    }).catch(() => undefined);
  }

  return eventId;
}
